import { DetailForm } from '@/@diffrag/form/DetailForm'
import { FormFieldType } from '@/@diffrag/form/fields/type'
import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentDetailView } from '@/@diffrag/layout/ContentDetailView'
import { api } from '@/api'
import { apiHelper } from '@/lib/api_helper'
import {
  IAdminBusinessInquiryUpdate,
  IAdminBusinessInquiryUpdateStatusEnum,
  IAdminClinicSave,
  IAdminClinicSaveStatusEnum,
  IAdminUserRO,
  IAdminUserROO1,
} from 'api'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AddressSearchButton } from './AddressSearchButton'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { toast } from '@/lib/toast'

export default function ClinicEdit() {
  const navigate = useNavigate()
  const id = useQueryValue('id', '')

  const [isPasswordChange, setIsPasswordChange] = useState(id ? false : true)

  const { data } = useApi({
    id: 'clinicDetail',
    fn: api().adminClinic.adminClinicDetail,
    params: Number(id),
    enabled: !!id,
  })

  const fields = useMemo(() => {
    const _fields = [
      {
        label: '아이디',
        key: 'email',
        type: FormFieldType.STRING,
        required: true,
        readOnly: !!id,
      },
    ]

    if (!id) {
      _fields.push({
        label: '비밀번호',
        key: 'password',
        type: FormFieldType.STRING,
        required: true,
        secure: true,
      })
    }

    _fields.push(
      ...[
        {
          label: '카테고리',
          key: 'clinic.category',
          type: FormFieldType.RELATION,
          required: true,
          searchApiFn: async ({ searchText, amount, page }) => {
            const result = await api().adminCategory.adminCategoryList({})
            return {
              rows: result.items,
              page: 1,
              total: result.total,
            }
          },
          labelKey: 'name',
        },
        {
          label: '지역',
          key: 'clinic.region',
          type: FormFieldType.RELATION,
          required: true,

          searchApiFn: async ({ searchText, amount, page }) => {
            const result = await api().adminRegion.adminRegionList({})
            return {
              rows: result.items,
              page: 1,
              total: result.total,
            }
          },
          labelKey: 'name',
        },
        {
          label: '병원 CI',
          key: 'clinic.ci',
          type: FormFieldType.FILE,
          required: true,
          accept: 'image/*',
          uploadHandler: apiHelper.uploadImage,
        },
        {
          label: '주소',
          key: 'clinic.address',
          type: FormFieldType.STRING,
          required: true,
        },
        {
          label: '상세주소',
          key: 'clinic.addressDetail',
          type: FormFieldType.STRING,
        },
        {
          label: '사업자등록번호',
          key: 'clinic.businessRegistrationNumber',
          type: FormFieldType.STRING,
          required: true,
        },
        {
          label: '사업자등록증',
          key: 'clinic.businessRegistrationCertificateId',
          type: FormFieldType.FILE,
          required: true,
          accept: 'image/*',
          uploadHandler: async (file: any) => {
            const res = await api().assets.uploadCreate({
              file: file.file,
              crop: JSON.stringify([file.crop]),
            })

            console.log(res, 'res')
            return res.id
          },
        },
        {
          label: '대표자명',
          key: 'clinic.representativeName',
          type: FormFieldType.STRING,
          required: true,
        },
        {
          label: '담당자명',
          key: 'clinic.managerName',
          type: FormFieldType.STRING,
          required: true,
        },
        {
          label: '담당자연락처',
          key: 'clinic.managerPhone',
          type: FormFieldType.STRING,
          required: true,
        },
        {
          label: '연락처',
          key: 'clinic.phone',
          type: FormFieldType.STRING,
          required: true,
        },

        {
          label: '상태',
          key: 'clinic.status',
          type: FormFieldType.SELECT,
          options: [
            { label: '활성', value: 'ACTIVE' },
            { label: '제휴종료', value: 'INACTIVE' },
          ],
        },
      ],
    )

    return _fields
  }, [id, data])

  const onSave = async (data: IAdminUserRO) => {
    console.log(data, 'data')
    if (isPasswordChange && data.password === '') {
      alert('비밀번호를 입력해주세요.')
      return
    }

    const updateData: IAdminClinicSave = {
      email: data.email ?? '',
      password: isPasswordChange ? data.password : '',
      clinic: {
        status: data.clinic.status,
        ciId: data.clinic?.ci.id ?? 0,
        regionId: data.clinic?.region.id ?? 0,
        categoryId: data.clinic?.category.id ?? 0,
        name: data.clinic?.name ?? '',
        nameZhcn: data.clinic?.nameZhcn ?? '',
        nameZhtw: data.clinic?.nameZhtw ?? '',
        nameEn: data.clinic?.nameEn ?? '',
        nameJa: data.clinic?.nameJa ?? '',
        nameTh: data.clinic?.nameTh ?? '',
        nameMn: data.clinic?.nameMn ?? '',
        nameRu: data.clinic?.nameRu ?? '',
        address: data.clinic?.address ?? '',
        addressZhcn: data.clinic?.addressZhcn ?? '',
        addressZhtw: data.clinic?.addressZhtw ?? '',
        addressEn: data.clinic?.addressEn ?? '',
        addressJa: data.clinic?.addressJa ?? '',
        addressTh: data.clinic?.addressTh ?? '',
        addressMn: data.clinic?.addressMn ?? '',
        addressRu: data.clinic?.addressRu ?? '',
        addressDetail: data.clinic?.addressDetail ?? '',
        addressDetailZhcn: data.clinic?.addressDetailZhcn ?? '',
        addressDetailZhtw: data.clinic?.addressDetailZhtw ?? '',
        addressDetailEn: data.clinic?.addressDetailEn ?? '',
        addressDetailJa: data.clinic?.addressDetailJa ?? '',
        addressDetailTh: data.clinic?.addressDetailTh ?? '',
        addressDetailMn: data.clinic?.addressDetailMn ?? '',
        addressDetailRu: data.clinic?.addressDetailRu ?? '',
        businessRegistrationNumber:
          data.clinic?.businessRegistrationNumber ?? '',
        businessRegistrationCertificateId:
          typeof data.clinic?.businessRegistrationCertificate === 'number'
            ? data.clinic.businessRegistrationCertificate
            : (data.clinic?.businessRegistrationCertificate?.id ?? 0),
        representativeName: data.clinic?.representativeName ?? '',
        managerName: data.clinic?.managerName ?? '',
        managerPhone: data.clinic?.managerPhone ?? '',
        phone: data.clinic?.phone ?? '',
      },
    }
    try {
      if (id) {
        const res = await api().adminClinic.adminClinicCreate2(
          Number(id),
          updateData,
        )
      } else {
        const res = await api().adminClinic.adminClinicCreate(updateData)
      }
      toast.success('제휴병원 수정을 완료하였습니다.')
      navigate(`/clinics`, { replace: true })
    } catch (error) {
      console.log(error)
      if (error.error.message === 'User already exists') {
        alert('이미 등록된 아이디입니다.')
      }
      if (error.error.message === 'Clinic already exists') {
        alert('이미 등록된 병원입니다.')
      }
    }
  }

  return (
    <ContentDetailView
      title={`제휴병원 ${id ? '수정' : '등록'}`}
      data={data}
      onSave={onSave}
      hideSaveToast={() => true}
    >
      <DetailForm
        data={data}
        fields={[
          {
            label: '아이디',
            key: 'email',
            type: FormFieldType.STRING,
            required: true,
            readOnly: !!id,
          },
          {
            label: '비밀번호',
            key: 'password',
            type: FormFieldType.CUSTOM_READ_ONLY,
            required: !!id || isPasswordChange,
            secure: true,
            render(data, context) {
              console.log(data)
              return (
                <div className="flex items-center gap-2">
                  {isPasswordChange && (
                    <Input
                      {...context?.register('password')}
                      className="w-full"
                      type="password"
                      autoComplete="new-password"
                      placeholder="새 비밀번호를 입력하세요"
                      key={
                        isPasswordChange ? 'password-change' : 'password-hidden'
                      }
                    />
                  )}
                  {id && !isPasswordChange && (
                    <Button
                      onClick={() => {
                        setIsPasswordChange(true)
                        context?.setValue('password', '') // 비밀번호 필드 초기화
                      }}
                      type="button"
                    >
                      비밀번호 변경
                    </Button>
                  )}
                </div>
              )
            },
          },
          {
            label: '카테고리',
            key: 'clinic.category',
            type: FormFieldType.RELATION,
            required: true,
            searchApiFn: async ({ searchText, amount, page }) => {
              const result = await api().adminCategory.adminCategoryList({})
              return {
                rows: result.items,
                page: 1,
                total: result.total,
              }
            },
            labelKey: 'name',
          },
          {
            label: '지역',
            key: 'clinic.region',
            type: FormFieldType.RELATION,
            required: true,

            searchApiFn: async ({ searchText, amount, page }) => {
              const result = await api().adminRegion.adminRegionList({})
              return {
                rows: result.items,
                page: 1,
                total: result.total,
              }
            },
            labelKey: 'name',
          },
          {
            label: '병원명 (한국어)',
            key: 'clinic.name',
            type: FormFieldType.STRING,
            required: true,
          },
          {
            label: '병원명 (简体中文)',
            key: 'clinic.nameZhcn',
            type: FormFieldType.STRING,
          },
          {
            label: '병원명 (繁體中文)',
            key: 'clinic.nameZhtw',
            type: FormFieldType.STRING,
          },
          {
            label: '병원명 (English)',
            key: 'clinic.nameEn',
            type: FormFieldType.STRING,
          },
          {
            label: '병원명 (日本語)',
            key: 'clinic.nameJa',
            type: FormFieldType.STRING,
          },
          {
            label: '병원명 (ไทย)',
            key: 'clinic.nameTh',
            type: FormFieldType.STRING,
          },
          {
            label: '병원명 (Монгол)',
            key: 'clinic.nameMn',
            type: FormFieldType.STRING,
          },
          {
            label: '병원명 (Русский)',
            key: 'clinic.nameRu',
            type: FormFieldType.STRING,
          },

          {
            label: '병원 CI',
            key: 'clinic.ci',
            type: FormFieldType.FILE,
            required: true,
            accept: 'image/*',
            uploadHandler: apiHelper.uploadImage,
          },
          {
            label: '주소 (한국어)',
            key: 'clinic.address',
            type: FormFieldType.CUSTOM_READ_ONLY,
            required: true,
            render: (data, context) => {
              return (
                <div className="flex items-center gap-2">
                  <span>{data?.clinic.address}</span>
                  <AddressSearchButton
                    onComplete={(address) => {
                      context.setValue('clinic.address', address)
                    }}
                  />
                </div>
              )
            },
          },
          {
            label: '상세주소 (한국어)',
            key: 'clinic.addressDetail',
            type: FormFieldType.STRING,
          },
          {
            label: '주소 (简体中文)',
            key: 'clinic.addressZhcn',
            type: FormFieldType.STRING,
          },
          {
            label: '상세주소 (简体中文)',
            key: 'clinic.addressDetailZhcn',
            type: FormFieldType.STRING,
          },
          {
            label: '주소 (繁體中文)',
            key: 'clinic.addressZhtw',
            type: FormFieldType.STRING,
          },
          {
            label: '상세주소 (繁體中文)',
            key: 'clinic.addressDetailZhtw',
            type: FormFieldType.STRING,
          },
          {
            label: '주소 (English)',
            key: 'clinic.addressEn',
            type: FormFieldType.STRING,
          },
          {
            label: '상세주소 (English)',
            key: 'clinic.addressDetailEn',
            type: FormFieldType.STRING,
          },
          {
            label: '주소 (日本語)',
            key: 'clinic.addressJa',
            type: FormFieldType.STRING,
          },
          {
            label: '상세주소 (日本語)',
            key: 'clinic.addressDetailJa',
            type: FormFieldType.STRING,
          },
          {
            label: '주소 (ไทย)',
            key: 'clinic.addressTh',
            type: FormFieldType.STRING,
          },
          {
            label: '상세주소 (ไทย)',
            key: 'clinic.addressDetailTh',
            type: FormFieldType.STRING,
          },
          {
            label: '주소 (Монгол)',
            key: 'clinic.addressMn',
            type: FormFieldType.STRING,
          },
          {
            label: '상세주소 (Монгол)',
            key: 'clinic.addressDetailMn',
            type: FormFieldType.STRING,
          },
          {
            label: '주소 (Русский)',
            key: 'clinic.addressRu',
            type: FormFieldType.STRING,
          },
          {
            label: '상세주소 (Русский)',
            key: 'clinic.addressDetailRu',
            type: FormFieldType.STRING,
          },
          {
            label: '사업자등록번호',
            key: 'clinic.businessRegistrationNumber',
            type: FormFieldType.STRING,
            required: true,
          },
          {
            label: '사업자등록증',
            key: 'clinic.businessRegistrationCertificate',
            type: FormFieldType.FILE,
            required: true,
            accept: 'image/*',
            async uploadHandler(file) {
              const res = await api().assets.uploadCreate({
                file: file.file,
                crop: JSON.stringify([file.crop]),
              })
              return res.id
            },
          },
          {
            label: '대표자명',
            key: 'clinic.representativeName',
            type: FormFieldType.STRING,
            required: true,
          },
          {
            label: '담당자명',
            key: 'clinic.managerName',
            type: FormFieldType.STRING,
            required: true,
          },
          {
            label: '담당자연락처',
            key: 'clinic.managerPhone',
            type: FormFieldType.STRING,
            required: true,
          },
          {
            label: '연락처',
            key: 'clinic.phone',
            type: FormFieldType.STRING,
            required: true,
          },

          {
            label: '상태',
            key: 'clinic.status',
            type: FormFieldType.SELECT,
            options: [
              { label: '활성', value: 'ACTIVE' },
              { label: '제휴종료', value: 'INACTIVE' },
            ],
            required: true,
          },
        ]}
      />
    </ContentDetailView>
  )
}
